import type { SwapTransaction } from 'api/changelly'

// Pure helpers for the Fund-with-crypto flow: unit conversion, validation,
// formatting and the status → step mapping. No React, no network — so the
// arithmetic (BigInt sats, USD assist) is unit-testable on its own.

export const AMOUNT_UNITS: Record<string, string> = {
    btc: 'sats',
    eth: 'ETH',
    sol: 'SOL',
    ltc: 'LTC',
    bch: 'BCH',
    zec: 'ZEC',
    etn: 'ETN',
    usdcsol: 'USDC',
    usdtrx: 'USDT',
}
export const COIN_TICKERS: Record<string, string> = {
    btc: 'BTC',
    eth: 'ETH',
    sol: 'SOL',
    ltc: 'LTC',
    bch: 'BCH',
    zec: 'ZEC',
    etn: 'ETN',
    usdcsol: 'USDC',
    usdtrx: 'USDT',
}
export const COIN_NETWORKS: Record<string, string> = {
    btc: 'Bitcoin',
    eth: 'Ethereum',
    sol: 'Solana',
    ltc: 'Litecoin',
    bch: 'Bitcoin Cash',
    zec: 'Zcash',
    etn: 'Electroneum',
    usdcsol: 'Solana',
    usdtrx: 'Tron',
}

export const STATUS_LABELS: Record<string, string> = {
    creating: 'Preparing deposit instructions…',
    creation_unknown:
        'Swap creation could not be confirmed. No payment instructions are available.',
    new: 'Waiting for your payment',
    waiting: 'Waiting for your payment',
    confirming: 'Payment received — waiting for confirmations',
    exchanging: 'Exchanging your crypto for BSV',
    sending: 'Sending BSV to your wallet',
    finished: 'Complete — BSV sent to your wallet',
    hold: 'Action required: identity verification',
    failed: 'Swap failed — contact Changelly for help',
    refunded: 'Payment refunded',
    overdue: 'Payment window has ended — do not send funds',
    expired: 'Swap expired — do not send funds',
}

export const ACTIVE_STATUSES = new Set([
    'creating',
    'new',
    'waiting',
    'confirming',
    'exchanging',
    'sending',
    'hold',
])
export const PAYMENT_WINDOW_MS = 3 * 60 * 60 * 1000
export const STAGE_LABELS = ['Pay', 'Confirm', 'Exchange', 'Send', 'Done']

export function coinTicker(from: string) {
    return COIN_TICKERS[from] || from.toUpperCase()
}

/** Network name: the known chains by name, else whatever the API label says ("XYZ (Foo network)"). */
export function coinNetwork(from: string, label?: string | null) {
    const fromLabel = /\(([^)]+?) network\)/.exec(label || '')?.[1]
    return COIN_NETWORKS[from] || fromLabel || coinTicker(from)
}

export function nativeUnit(from: string) {
    return AMOUNT_UNITS[from] || from.toUpperCase()
}

/** Whole sats for Bitcoin, decimals elsewhere — the input accepts nothing else. */
export function acceptsAmount(from: string, value: string) {
    return from === 'btc' ? /^\d{0,18}$/.test(value) : /^\d{0,10}(?:\.\d{0,8})?$/.test(value)
}

export function acceptsUsd(value: string) {
    return /^\d{0,10}(?:\.\d{0,2})?$/.test(value)
}

/** Provider amount (coin units, string) for a typed native amount. */
export function quoteAmount(from: string, amount: string) {
    if (from !== 'btc') return amount.replace(/^\./, '0.').replace(/\.$/, '')
    const value = amount.trim()
    if (!/^\d{1,18}$/.test(value) || BigInt(value) === 0n) {
        throw new Error('Enter a positive whole number of sats (up to 18 digits).')
    }
    return amountFromAtoms(BigInt(value))
}

/** Coin units (≤ 8 decimals) as an exact integer count of 1e-8 atoms. */
export function currencyAtoms(value: string) {
    const parts = /^(\d+)(?:\.(\d{1,8}))?$/.exec(value)
    if (!parts) throw new Error('Invalid swap amount.')
    return BigInt(parts[1] + (parts[2] || '').padEnd(8, '0'))
}

/** Coin units (string) for an exact atom count — the inverse of currencyAtoms. */
export function amountFromAtoms(atoms: bigint) {
    const fraction = (atoms % 100000000n).toString().padStart(8, '0').replace(/0+$/, '')
    return `${atoms / 100000000n}${fraction ? `.${fraction}` : ''}`
}

/** Input text for a provider amount (sats for Bitcoin, coin units elsewhere). */
export function inputAmount(from: string, value: string) {
    return from === 'btc' ? currencyAtoms(value).toString() : trimZeros(value)
}

export function formatAmount(from: string, value: string) {
    return from === 'btc'
        ? `${currencyAtoms(value).toLocaleString('en-US')} sats`
        : `${trimZeros(value)} ${nativeUnit(from)}`
}

export function formatUsd(value: number) {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

/** "$100" for whole dollars, "$2.37" otherwise — chip labels drop empty cents. */
export function formatUsdShort(value: number) {
    return value % 1 === 0 ? `$${value.toLocaleString('en-US')}` : formatUsd(value)
}

export function trimZeros(value: string) {
    return value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value
}

export function formatBsv(value: string | null | undefined) {
    if (!value) return null
    return trimZeros(value)
}

export function sentAmount(
    tx: Pick<SwapTransaction, 'from_currency' | 'from_amount' | 'currency_label'>,
) {
    if (tx.from_currency === 'btc') {
        const parts = /^(\d+)(?:\.(\d{1,8}))?$/.exec(tx.from_amount)
        if (parts) {
            const sats = BigInt(parts[1] + (parts[2] || '').padEnd(8, '0'))
            return `${sats.toLocaleString('en-US')} sats (BTC network)`
        }
    }
    return `${tx.from_amount} ${tx.currency_label}`
}

/** Short "100,000 sats" / "0.05 ETH" form of what the user must send. */
export function sentAmountShort(tx: Pick<SwapTransaction, 'from_currency' | 'from_amount'>) {
    try {
        return formatAmount(tx.from_currency, tx.from_amount)
    } catch (_) {
        return `${tx.from_amount} ${nativeUnit(tx.from_currency)}`
    }
}

/** USD per one coin, from a quote's BSV-per-coin rate and the BSV reference price. */
export function usdPerCoin(
    rateBsv: string | null | undefined,
    usdPerBsv: number | null | undefined,
) {
    const rate = Number(rateBsv)
    if (!usdPerBsv || !Number.isFinite(usdPerBsv) || usdPerBsv <= 0) return null
    if (!Number.isFinite(rate) || rate <= 0) return null
    return rate * usdPerBsv
}

/** Coin units as a float for display math (never for the provider amount). */
export function coinUnitsFromInput(from: string, value: string) {
    if (!value) return 0
    if (from === 'btc') return Number(value) / 1e8
    return Number(value)
}

export function inputToUsd(from: string, value: string, perCoin: number | null) {
    if (!perCoin || !value) return null
    const units = coinUnitsFromInput(from, value)
    if (!Number.isFinite(units) || units <= 0) return null
    return units * perCoin
}

/** Native input text that is worth `usd` dollars at `perCoin` USD per coin. */
export function usdToInput(from: string, usd: number, perCoin: number | null) {
    if (!perCoin || !Number.isFinite(usd) || usd <= 0) return ''
    const units = usd / perCoin
    if (from === 'btc') return String(Math.max(1, Math.round(units * 1e8)))
    return trimZeros(units.toFixed(8)) || '0'
}

/** Whether a dollar amount converts to a provider amount on or above `minimum`. */
function usdClearsMinimum(from: string, usd: number, minimum: string, perCoin: number | null) {
    try {
        return (
            currencyAtoms(quoteAmount(from, usdToInput(from, usd, perCoin))) >=
            currencyAtoms(minimum)
        )
    } catch (_) {
        return false
    }
}

/**
 * The Min chip's dollar figure: the minimum at `perCoin`, rounded up (to the
 * dollar from $10, to the cent below) so filling or typing it converts back on
 * or above the minimum rather than a hair under it.
 */
export function minimumUsd(from: string, minimum: string, perCoin: number | null) {
    const usd = inputToUsd(from, inputAmount(from, minimum), perCoin)
    if (!usd) return null
    const step = usd >= 10 ? 1 : 0.01
    let value = Number((Math.ceil(usd / step - 1e-9) * step).toFixed(2))
    for (let i = 0; i < 3 && !usdClearsMinimum(from, value, minimum, perCoin); i++)
        value = Number((value + step).toFixed(2))
    return value
}

// "Max" fills this far under the limit so the swap still clears the server's
// cap check (which allows 1%) when the price moves between the reference rate
// and the quote.
export const MAX_FILL_MARGIN_PERCENT = 3

/** The Max chip's provider amount: `maximum` less the margin, or the exact maximum when that would fall under `minimum`. */
export function maxFillAmount(maximum: string, minimum: string) {
    const max = currencyAtoms(maximum)
    const shaved = (max * BigInt(100 - MAX_FILL_MARGIN_PERCENT)) / 100n
    return amountFromAtoms(shaved >= currencyAtoms(minimum) ? shaved : max)
}

/** The Max chip's dollar figure: `usd` less the margin, floored to the cent, or the floored `usd` when that would convert under `minimum`. */
export function maxFillUsd(from: string, usd: number, minimum: string, perCoin: number | null) {
    const cents = Math.round(usd * 100)
    const shaved = Math.floor((cents * (100 - MAX_FILL_MARGIN_PERCENT)) / 100) / 100
    if (usdClearsMinimum(from, shaved, minimum, perCoin)) return shaved
    return Math.floor(usd * 100) / 100
}

export function stageIndex(status: string) {
    switch (status) {
        case 'creating':
        case 'new':
        case 'waiting':
            return 0
        case 'confirming':
        case 'hold':
            return 1
        case 'exchanging':
            return 2
        case 'sending':
            return 3
        case 'finished':
            return 4
        default:
            return null
    }
}

export function paymentDeadline(tx: Pick<SwapTransaction, 'created_at'>) {
    return Date.parse(tx.created_at) + PAYMENT_WINDOW_MS
}

export function awaitingPayment(
    tx: Pick<SwapTransaction, 'status' | 'created_at'>,
    now = Date.now(),
) {
    return ['new', 'waiting'].includes(tx.status) && now < paymentDeadline(tx)
}

export function formatCountdown(ms: number) {
    const total = Math.max(0, Math.floor(ms / 1000))
    const h = Math.floor(total / 3600)
    const m = Math.floor((total % 3600) / 60)
    const s = total % 60
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/** Payment-URI for wallet apps that scan; plain address where no URI scheme carries amounts safely. */
export function qrPayload(
    tx: Pick<SwapTransaction, 'from_currency' | 'from_amount' | 'payin_address' | 'payin_extra_id'>,
) {
    const address = tx.payin_address
    if (!address) return null
    // A memo/tag must travel with the transfer; a URI cannot promise that, so scan the bare address.
    if (tx.payin_extra_id) return address
    switch (tx.from_currency) {
        case 'btc':
            return `bitcoin:${address}?amount=${tx.from_amount}`
        case 'ltc':
            return `litecoin:${address}?amount=${tx.from_amount}`
        case 'bch':
            // CashAddr URIs carry their own prefix; don't double it.
            return `${address.startsWith('bitcoincash:') ? '' : 'bitcoincash:'}${address}?amount=${
                tx.from_amount
            }`
        case 'zec':
            return `zcash:${address}?amount=${tx.from_amount}`
        case 'eth':
            return `ethereum:${address}@1`
        case 'sol':
            return `solana:${address}`
        // A Solana Pay URI without spl-token requests SOL, never USDC.
        default:
            return address
    }
}

/** Newest first, with anything still moving ahead of settled swaps. */
export function sortSwaps<T extends Pick<SwapTransaction, 'status' | 'created_at'>>(swaps: T[]) {
    return [...swaps].sort((a, b) => {
        const activeA = ACTIVE_STATUSES.has(a.status) ? 0 : 1
        const activeB = ACTIVE_STATUSES.has(b.status) ? 0 : 1
        if (activeA !== activeB) return activeA - activeB
        return Date.parse(b.created_at) - Date.parse(a.created_at)
    })
}

/** Keep a newer server snapshot when history, polling and pushes arrive out of order. */
export function latestSwap(
    previous: SwapTransaction | null | undefined,
    incoming: SwapTransaction,
) {
    if (!previous || previous.id !== incoming.id) return incoming
    const previousTime = Date.parse(previous.updated_at || '')
    const incomingTime = Date.parse(incoming.updated_at || '')
    if (
        Number.isFinite(previousTime) &&
        (!Number.isFinite(incomingTime) || previousTime > incomingTime)
    )
        return previous
    return incoming
}
